import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { API_URL } from '@/lib/api';

const StoreForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [formData, setFormData] = useState({
    store_name: '',
    store_id: '',
    password: '',
    owner_name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    gstin: '',
    gstBusinessName: '',
    gstAddress: '',
    status: 'active'
  });

  useEffect(() => {
    if (isEdit) {
      fetchStore();
    }
  }, [id]);

  const fetchStore = async () => {
    try {
      const res = await axios.get(`${API_URL}/stores/${id}`);
      const d = res.data;
      setFormData({
        store_name: d.storeName || '',
        store_id: d.storeId || '',
        password: '',
        owner_name: d.ownerName || '',
        phone: d.phone || '',
        address: d.address || '',
        city: d.city || '',
        state: d.state || '',
        pincode: d.pincode || '',
        gstin: d.gstin || '',
        gstBusinessName: d.gstBusinessName || '',
        gstAddress: d.gstAddress || '',
        status: d.status === true ? 'active' : 'blocked'
      });
    } catch (error) {
      toast.error('Failed to load store');
      navigate('/stores');
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const gstPayload = {
        gstin: formData.gstin,
        gstBusinessName: formData.gstBusinessName,
        gstAddress: formData.gstAddress,
      };
      if (isEdit) {
        await axios.put(`${API_URL}/stores/${id}`, {
          storeName: formData.store_name,
          ownerName: formData.owner_name,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          status: formData.status === 'active',
          ...gstPayload,
        });
        toast.success('Store updated successfully');
      } else {
        await axios.post(`${API_URL}/stores`, {
          storeName: formData.store_name,
          storeId: formData.store_id,
          password: formData.password,
          ownerName: formData.owner_name,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          ...gstPayload,
        });
        toast.success('Store created successfully');
      }
      navigate('/stores');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save store';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-950"></div>
      </div>
    );
  }

  return (
    <div data-testid="store-form-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/stores')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="page-title">{isEdit ? 'Edit Store' : 'Create Store'}</h1>
          <p className="text-slate-500 mt-1">
            {isEdit ? 'Update store information' : 'Add a new distributor store'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Store Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="store_name" className="form-label">Store Name *</Label>
                  <Input
                    id="store_name"
                    name="store_name"
                    value={formData.store_name}
                    onChange={handleChange}
                    placeholder="Enter store name"
                    required
                    data-testid="store-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="store_id" className="form-label">Store ID *</Label>
                  <Input
                    id="store_id"
                    name="store_id"
                    value={formData.store_id}
                    onChange={handleChange}
                    placeholder="e.g., STR001"
                    required
                    disabled={isEdit}
                    data-testid="store-id-input"
                  />
                </div>
              </div>

              {!isEdit && (
                <div>
                  <Label htmlFor="password" className="form-label">Password *</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter password for store login"
                    required={!isEdit}
                    data-testid="password-input"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="owner_name" className="form-label">Owner Name *</Label>
                  <Input
                    id="owner_name"
                    name="owner_name"
                    value={formData.owner_name}
                    onChange={handleChange}
                    placeholder="Store owner's name"
                    required
                    data-testid="owner-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="form-label">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="e.g., 9876543210"
                    required
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="address" className="form-label">Address *</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Full address"
                  required
                  data-testid="address-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <Label htmlFor="city" className="form-label">City *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                    required
                    data-testid="city-input"
                  />
                </div>
                <div>
                  <Label htmlFor="state" className="form-label">State *</Label>
                  <Input
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="State"
                    required
                    data-testid="state-input"
                  />
                </div>
                <div>
                  <Label htmlFor="pincode" className="form-label">Pincode *</Label>
                  <Input
                    id="pincode"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    placeholder="e.g., 400001"
                    required
                    data-testid="pincode-input"
                  />
                </div>
              </div>
              {/* GST Details */}
              <div className="pt-2">
                <p className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-md bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center">G</span>
                  GST Details <span className="text-slate-400 font-normal">(optional)</span>
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="gstin" className="form-label">GSTIN</Label>
                    <Input
                      id="gstin"
                      name="gstin"
                      value={formData.gstin}
                      onChange={handleChange}
                      placeholder="e.g., 27AABCU9603R1ZX"
                      maxLength={15}
                    />
                    <p className="text-xs text-slate-400 mt-1">15-character GST Identification Number</p>
                  </div>
                  <div>
                    <Label htmlFor="gstBusinessName" className="form-label">GST Business Name</Label>
                    <Input
                      id="gstBusinessName"
                      name="gstBusinessName"
                      value={formData.gstBusinessName}
                      onChange={handleChange}
                      placeholder="Legal business name as per GST"
                    />
                  </div>
                </div>
                <div className="mt-5">
                  <Label htmlFor="gstAddress" className="form-label">GST Registered Address</Label>
                  <Input
                    id="gstAddress"
                    name="gstAddress"
                    value={formData.gstAddress}
                    onChange={handleChange}
                    placeholder="Address as per GST registration"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Status Card */}
          <Card>
            <CardHeader>
              <CardTitle>Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Store Active</p>
                  <p className="text-sm text-slate-500">
                    {formData.status === 'active' ? 'Store can place orders' : 'Store is blocked'}
                  </p>
                </div>
                <Switch
                  checked={formData.status === 'active'}
                  onCheckedChange={(checked) => 
                    setFormData(prev => ({ ...prev, status: checked ? 'active' : 'blocked' }))
                  }
                  data-testid="status-toggle"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4 mt-8">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate('/stores')}
            data-testid="cancel-btn"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            className="bg-purple-950 hover:bg-purple-900"
            disabled={loading}
            data-testid="submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              isEdit ? 'Update Store' : 'Create Store'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default StoreForm;
